/**
 * Shared layout constants for upload flow cards.
 * Optimized for iOS webview embedding - compact sizing.
 * Keeps card + logo container fixed across upload/uploading/processing/done/error states.
 *
 * Total content height = CARD_HEIGHT (branding lives inside the card)
 * = 240px (fixed across all states)
 */
export const CARD_HEIGHT = 240;

/** Transparent for seamless iframe/webview embedding */
export const UPLOAD_FLOW_PAGE_BG = 'transparent';

export const CARD_MAX_WIDTH = 360;

/** Rounded shell on `/main` bento (folders list, floor plans panel, project tiles). */
export const BENTO_SURFACE_BORDER_RADIUS = "24px";

/**
 * Insets inside the glass card — Apple HIG–style minimums for embedded iOS:
 * ~16pt horizontal margin to content, ~12pt compact top/bottom so 280px-tall cards fit without scrolling.
 */
export const CARD_INSET_HORIZONTAL_PX = 16;
export const CARD_INSET_TOP_PX = 2;
export const CARD_INSET_BOTTOM_PX = 2;

export const GAP_CARD_TO_LOGO_PX = 14;
export const LOGO_SECTION_HEIGHT = 48;

/** Typography — scaled for iOS webview at 300px card width */
export const TITLE_FONT_SIZE = '17px';
export const SUBTITLE_FONT_SIZE = '13px';
export const BUTTON_FONT_SIZE = '15px';
export const SECONDARY_BUTTON_FONT_SIZE = '13px';

/** Normalized letter-spacing for all text */
export const LETTER_SPACING = '-0.01em';
export const LOGO_FONT_SIZE = '9px';
export const LOGO_SIZE = 32;

/** Icon sizes */
export const ICON_LARGE = 41;
export const ICON_SMALL = 24; // BarSpinner container; bars scaled to match
export const PROGRESS_BAR_HEIGHT = 7;
export const SUCCESS_RING_PX = 56; // outer; scaled for 280px card without scroll
export const SUCCESS_INNER_PX = 52;

/** Label colors — light card palette */
export const LABEL_PRIMARY = '#181818';
export const LABEL_SECONDARY = '#6e6e73';
export const LABEL_TERTIARY = '#6e6e73';

/**
 * Shared “liquid glass” panel for external AI upload flow cards only.
 * Frosted surface + specular rim; used for the main panel on each upload-state card.
 */
export const externalFlowGlassCardLayoutSx = {
  borderRadius: '22px',
  boxSizing: 'border-box',
  px: `${CARD_INSET_HORIZONTAL_PX}px`,
  pt: `${CARD_INSET_TOP_PX}px`,
  pb: `${CARD_INSET_BOTTOM_PX}px`,
  height: CARD_HEIGHT,
  minHeight: CARD_HEIGHT,
  maxHeight: CARD_HEIGHT,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  textAlign: 'center',
  overflow: 'hidden',
  position: 'relative',
  flexShrink: 0,
  transition: 'background 0.3s ease, border-color 0.3s ease',
};

export const externalFlowGlassSurfaceSx = {
  background: 'linear-gradient(160deg, #f5f5f7 0%, #e8e8ed 35%, #d8d8e0 70%, #ccccD4 100%)',
  border: 'none',
  boxShadow: 'none',
  transition: 'background 0.4s ease',
};

/** Flat surface for requirement-family cards (unsupported + guide) — matches SVG fill */
export const REQUIREMENT_CARD_SURFACE_BG = '#FAFAFA';

export const externalFlowRequirementSurfaceSx = {
  background: REQUIREMENT_CARD_SURFACE_BG,
  border: 'none',
  boxShadow: 'none',
};

export const externalFlowGlassDragSx = {
  background: 'linear-gradient(160deg, #fafafa 0%, #ededed 35%, #e0e0e5 70%, #d5d5da 100%)',
  border: 'none',
  boxShadow: 'none',
};

/** Circular plate behind hero icons — light theme variant. */
export const externalFlowIconGlassWellSx = (opts = {}) => {
  const { size = 72, borderRadius = '50%' } = opts;
  return {
    width: size,
    height: size,
    minWidth: size,
    minHeight: size,
    borderRadius,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0, 0, 0, 0.05)',
    border: '0.5px solid rgba(0, 0, 0, 0.08)',
    boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.04)',
  };
};

/** Success hero — light circle */
export const externalFlowSuccessIconWellSx = {
  width: SUCCESS_INNER_PX,
  height: SUCCESS_INNER_PX,
  minWidth: SUCCESS_INNER_PX,
  minHeight: SUCCESS_INNER_PX,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(0, 0, 0, 0.04)',
  border: '0.5px solid rgba(0, 0, 0, 0.08)',
  boxShadow: 'none',
};

/** Success ring host — static center; only the backdrop layer spins */
export const externalFlowSuccessRingContainerSx = {
  width: SUCCESS_RING_PX,
  height: SUCCESS_RING_PX,
  minWidth: SUCCESS_RING_PX,
  minHeight: SUCCESS_RING_PX,
  position: 'relative',
  flexShrink: 0,
};

export const externalFlowSuccessRingSpinBackdropSx = {
  position: 'absolute',
  inset: 0,
  borderRadius: '50%',
  background:
    'conic-gradient(from 0deg, rgba(52,199,89,0.35), rgba(52,199,89,0.10) 25%, transparent 46%, rgba(52,199,89,0.10) 70%, rgba(52,199,89,0.35))',
  animation: 'mfExternalSuccessRing 2.4s linear infinite',
  pointerEvents: 'none',
  willChange: 'transform',
  '@keyframes mfExternalSuccessRing': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
};

export const externalFlowSuccessIconInnerWellSx = {
  ...externalFlowSuccessIconWellSx,
  width: '100%',
  height: '100%',
  minWidth: 0,
  minHeight: 0,
};

/** Primary CTA — white liquid glass pill. */
export const externalFlowPrimaryButtonSx = {
  borderRadius: '980px',
  paddingLeft: '24px',
  paddingRight: '24px',
  py: 1.125,
  fontSize: BUTTON_FONT_SIZE,
  fontWeight: 600,
  letterSpacing: LETTER_SPACING,
  textTransform: 'none !important',
  width: 'fit-content',
  minWidth: 140,
  color: '#181818',
  background: 'rgba(255,255,255,0.95)',
  border: '0.5px solid rgba(0, 0, 0, 0.08)',
  boxShadow: [
    'inset 0 1px 0 rgba(255, 255, 255, 0.95)',
    'inset 0 -1px 0 rgba(0, 0, 0, 0.03)',
    '0 1px 3px rgba(0, 0, 0, 0.08)',
    '0 1px 2px rgba(0, 0, 0, 0.06)',
  ].join(', '),
  transition: 'box-shadow 0.15s ease, background 0.15s ease',
  '&:hover': {
    background: 'rgba(255,255,255,0.75)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.12), 0 2px 4px rgba(0,0,0,0.08)',
  },
  '&:active': {
    background: 'rgba(255,255,255,0.60)',
  },
};

/** Progress track — light theme */
export const externalFlowLinearProgressTrackSx = {
  height: PROGRESS_BAR_HEIGHT,
  borderRadius: 100,
  bgcolor: 'rgba(0, 0, 0, 0.06)',
  border: '0.5px solid rgba(0, 0, 0, 0.06)',
  boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.04)',
  overflow: 'hidden',
  '& .MuiLinearProgress-bar': {
    background: 'linear-gradient(90deg, #181818 0%, #333333 100%)',
    borderRadius: 100,
    transition: 'transform 200ms ease-out',
  },
};

/** Vertical rhythm — tight but ≥8px between related items for iOS embedded 280px card, no scroll */
export const FLOW_MB_AFTER_ICON = 1.1;

/** Extra px under hero (spinner / sparkle / success ring) before title — uploading, processing, done only */
export const FLOW_ICON_TO_TITLE_EXTRA_PX = 10;
export const FLOW_MB_AFTER_TITLE = 0.5;
export const FLOW_MB_AFTER_SUBTITLE = 1.1;
export const FLOW_MB_AFTER_PROGRESS = 0.875;

/** Max 3 lines for embedded cards (no scroll, long API/server copy) */
export const externalFlowLineClamp3Sx = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 3,
  overflow: 'hidden',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
};

/** Inline validation / notice — light capsule with error tint */
export const externalFlowInlineAlertSx = {
  p: 1.25,
  width: '100%',
  maxWidth: 250,
  borderRadius: '16px',
  background: 'rgba(215, 0, 21, 0.06)',
  border: '0.5px solid rgba(215, 0, 21, 0.12)',
  boxShadow: 'none',
};

/**
 * Secondary text action shared by Cancel (uploading/processing) and
 * Is my plan compatible? (idle / unsupported). Matches the uploading Cancel look.
 */
export const externalFlowSecondaryTextButtonSx = {
  color: LABEL_SECONDARY,
  fontSize: SECONDARY_BUTTON_FONT_SIZE,
  fontWeight: 500,
  letterSpacing: LETTER_SPACING,
  textTransform: 'none !important',
  whiteSpace: 'nowrap',
  borderRadius: '14px',
  px: 2,
  py: 0.5,
  minWidth: 0,
  flexShrink: 0,
  '&:hover': {
    bgcolor: 'rgba(0, 0, 0, 0.04)',
    textDecoration: 'none',
    color: LABEL_PRIMARY,
  },
};

/** Circular chevron control for requirement card pagination */
export const externalFlowNavIconButtonSx = {
  width: 32,
  height: 32,
  padding: 0,
  color: LABEL_PRIMARY,
  background: 'rgba(255,255,255,0.95)',
  border: '0.5px solid rgba(0, 0, 0, 0.10)',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
  '&:hover': {
    background: 'rgba(255,255,255,0.75)',
  },
};

/**
 * Requirement family (0 unsupported + guide 1–3): illustration is designed
 * 110px tall, full card width, flush to the top. Title/subtitle always begin
 * at the same Y so cards share one rhythm.
 */
export const REQUIREMENT_ILLUSTRATION_HEIGHT = 110;
export const REQUIREMENT_TEXT_TOP_PX = 130;

/** Requirement-card subtitle — 1px under shared subtitle so copy stays on one line */
export const REQUIREMENT_SUBTITLE_FONT_SIZE = '12px';

/** Chevron control size inside the bottom CTA strip */
export const REQUIREMENT_NAV_ROW_HEIGHT = 32;

/**
 * Bottom CTA strip for requirement-0 + guide 1–3 — snapped to the card bottom
 * with actions nested inside so chevrons / Upload floor plan share one baseline.
 */
export const REQUIREMENT_CTA_STRIP_HEIGHT = 52;

/** Full-bleed top illustration shared by requirement-0 and guide cards */
export const requirementIllustrationSx = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  width: '100%',
  height: REQUIREMENT_ILLUSTRATION_HEIGHT,
  objectFit: 'fill',
  display: 'block',
  pointerEvents: 'none',
  userSelect: 'none',
  zIndex: 1,
};

/** Absolute bottom action strip — same nest for all requirement-family cards */
export const requirementCtaStripSx = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  height: REQUIREMENT_CTA_STRIP_HEIGHT,
  px: `${CARD_INSET_HORIZONTAL_PX}px`,
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  alignItems: 'center',
  columnGap: 1,
  boxSizing: 'border-box',
  zIndex: 3,
  flexShrink: 0,
};

/** Compact primary pill nested in the requirement CTA strip */
export const requirementCtaPrimaryButtonSx = {
  ...externalFlowPrimaryButtonSx,
  whiteSpace: 'nowrap',
  minWidth: 0,
  minHeight: REQUIREMENT_NAV_ROW_HEIGHT,
  height: REQUIREMENT_NAV_ROW_HEIGHT,
  px: '16px',
  py: 0,
  fontSize: '13px',
};

/** In-card branding text — sits at darkest end of the gradient */
export const externalFlowInCardBrandingSx = {
  fontSize: '11px',
  fontWeight: 400,
  color: '#8E8E93',
  lineHeight: 1.3,
  textAlign: 'center',
  letterSpacing: '0.01em',
  flexShrink: 0,
  pb: '6px',
};

/** Total fixed height — branding now lives inside the card, no external strip */
export const CONTENT_BLOCK_HEIGHT = CARD_HEIGHT;

/** Full-viewport shell — keeps the flow on one screen without page scrollbars */
export const externalFlowPageShellSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  minHeight: '100dvh',
  height: '100dvh',
  maxHeight: '100dvh',
  overflow: 'hidden',
  boxSizing: 'border-box',
  bgcolor: 'transparent',
};

/** Glass card + logo column — bounded to viewport */
export const externalFlowContentColumnSx = {
  width: '100%',
  maxWidth: CARD_MAX_WIDTH,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'stretch',
  flexShrink: 0,
};
